import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import { useIsFirstRender, usePrevious } from '@mote/helpers'
import { isNumber } from '@mote/helpers/assertion'
import { __DEV__ } from '@mote/helpers/constants'
import { mergeClassnames } from '@mote/helpers/dom'

const fadeOpacity = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 }
})

const fadeColor = (startColor, endColor) =>
  keyframes({
    from: { borderColor: startColor, background: startColor },
    to: { borderColor: endColor, background: endColor }
  })

function range(count) {
  return Array(count)
    .fill(1)
    .map((_, index) => index + 1)
}

function evaluateValue(value) {
  return isNumber(value) ? `${value}px` : value
}

export const Skeleton = React.forwardRef((props, ref) => {
  const {
    startColor = '#ececf3',
    endColor = '#d1d1e0',
    isLoaded,
    fadeDuration = 0.4,
    fadeSpeed = 0.8,
    className,
    ...rest
  } = props
  const isFirstRender = useIsFirstRender()

  const wasPreviouslyLoaded = usePrevious(isLoaded)

  const _className = mergeClassnames('mote-skeleton', className)

  if (isLoaded) {
    const animation =
      isFirstRender || wasPreviouslyLoaded
        ? 'none'
        : css`
            ${fadeOpacity} ${fadeDuration}s
          `

    return (
      <SkeletonLoadedRoot
        ref={ref}
        className={_className}
        {...rest}
        animation={animation}
      />
    )
  }

  return (
    <SkeletonRoot
      ref={ref}
      className={_className}
      startColor={startColor}
      endColor={endColor}
      fadeSpeed={fadeSpeed}
      {...rest}
    />
  )
})

if (__DEV__) {
  Skeleton.displayName = 'Skeleton'
}

export const SkeletonText = props => {
  const {
    noOfLines = 3,
    spacing = 8,
    skeletonHeight = 8,
    className,
    startColor,
    endColor,
    isLoaded,
    fadeDuration,
    fadeSpeed,
    children,
    ...rest
  } = props

  const numbers = range(noOfLines)

  const getWidth = index => {
    if (noOfLines > 1) {
      return index === numbers.length ? '80%' : '100%'
    }
    return '100%'
  }

  const _className = mergeClassnames('mote-skeleton__group', className)

  return (
    <div className={_className} {...rest}>
      {numbers.map((number, index) => {
        if (isLoaded && index > 0) {
          return null
        }

        const sizeProps = isLoaded
          ? null
          : {
              marginBottom: number === numbers.length ? 0 : spacing,
              width: getWidth(number),
              height: skeletonHeight
            }

        return (
          <Skeleton
            key={numbers.length.toString() + number}
            startColor={startColor}
            endColor={endColor}
            isLoaded={isLoaded}
            fadeDuration={fadeDuration}
            fadeSpeed={fadeSpeed}
            style={sizeProps}
          >
            {index === 0 ? children : undefined}
          </Skeleton>
        )
      })}
    </div>
  )
}

if (__DEV__) {
  SkeletonText.displayName = 'SkeletonText'
}

export const SkeletonCircle = ({ size = 24, ...rest }) => (
  <SkeletonCircleRoot size={size} {...rest} />
)

if (__DEV__) {
  SkeletonCircle.displayName = 'SkeletonCircle'
}

const SkeletonRoot = styled.div`
  box-shadow: none;
  background-clip: padding-box;
  cursor: default;
  color: transparent;
  pointer-events: none;
  user-select: none;
  opacity: 0.7;
  border-radius: 2px;
  border-color: ${({ startColor }) => startColor};
  background: ${({ endColor }) => endColor};
  animation: ${({ fadeSpeed, startColor, endColor }) => css`
    ${fadeSpeed}s linear infinite alternate ${fadeColor(startColor, endColor)}
  `};

  &::before, &::after, *: {
    visibility: hidden;
  }
`

const SkeletonLoadedRoot = styled.div`
  animation: ${({ animation }) => animation};
`

const SkeletonCircleRoot = styled(Skeleton)`
  border-radius: 100%;

  ${({ size }) => css`
    width: ${evaluateValue(size)};
    height: ${evaluateValue(size)};
  `}
`

Skeleton.Text = SkeletonText
Skeleton.Circle = SkeletonCircle
